import { Link } from 'react-router-dom'

import { FxIcon } from '@/components/fx'
import { cn } from '@/lib/utils'

import { FolderActionsMenu } from './FolderActionsMenu'
import {
  documentCountLabel,
  formatDate,
  templateCountLabel,
  type ProjectNode,
} from './projectData'

/**
 * The indented list from the artboard, built as a **disclosure list** rather
 * than an ARIA `tree`.
 *
 * A `tree` widget owns its arrow keys and expects one focus stop for the
 * whole thing, which fights the per-row controls the design puts in every
 * row (a bookmark, a kebab, a link). Nested `<ul>`s with a real
 * `aria-expanded` button per project give the same structure, the same
 * keyboard reach, and controls that behave like controls.
 *
 * It renders **two levels — a project and the forms in it — because that is
 * what the schema holds.** The artboard draws a project inside a project;
 * `folders` has no `parent_id`, so a third level would be a client-side
 * fiction that vanishes on reload. The shape here (a node that renders its
 * children) takes a deeper tree unchanged once the column exists.
 */
export function ProjectTree({
  projects,
  favorites,
  expanded,
  onToggleExpanded,
  onToggleFavorite,
  onRename,
  onShare,
  onDelete,
}: {
  projects: ProjectNode[]
  favorites: ReadonlySet<string>
  expanded: ReadonlySet<string>
  onToggleExpanded: (folderId: string) => void
  onToggleFavorite: (folderId: string) => void
  onRename: (project: ProjectNode) => void
  onShare: (project: ProjectNode) => void
  onDelete: (project: ProjectNode) => void
}) {
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {projects.map((project) => {
        const id = project.folder.id
        const isOpen = expanded.has(id)
        const isFavorite = favorites.has(id)
        return (
          <li key={id}>
            <div className="flex min-h-11 items-center gap-3 rounded-sm py-1 pl-2">
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-3 rounded-sm text-left"
                aria-expanded={isOpen}
                onClick={() => onToggleExpanded(id)}
              >
                <FxIcon
                  name={isOpen ? 'expand_more' : 'chevron_right'}
                  size={22}
                  className="flex-none text-text"
                />
                <FxIcon name="folder" size={22} filled className="flex-none text-primary-tonal" />
                <span className="min-w-0 flex-1 truncate text-body-strong">
                  {project.folder.name}
                </span>
              </button>
              <span className="hidden w-[140px] flex-none text-small text-text-muted sm:block">
                {templateCountLabel(project.templateCount)}
              </span>
              <span className="hidden w-[110px] flex-none text-right text-small tabular-nums text-text-muted xs:block">
                {formatDate(project.folder.updated_at ?? project.folder.created_at)}
              </span>
              <button
                type="button"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-sm text-primary-text"
                aria-pressed={isFavorite}
                aria-label={`„${project.folder.name}“ als Favorit merken`}
                onClick={() => onToggleFavorite(id)}
              >
                <FxIcon name="bookmark" size={22} filled={isFavorite} />
              </button>
              <FolderActionsMenu
                name={project.folder.name}
                onRename={() => onRename(project)}
                onShare={() => onShare(project)}
                onDelete={() => onDelete(project)}
              />
            </div>

            {isOpen && (
              <ul className="m-0 flex list-none flex-col p-0">
                {project.templates.length === 0 ? (
                  <li className="py-2 pl-[70px] text-small text-text-muted">
                    Noch kein Formular in diesem Projekt.
                  </li>
                ) : (
                  project.templates.map((template) => (
                    <li
                      key={template.id}
                      className="flex min-h-11 items-center gap-3 py-1 pl-[42px]"
                    >
                      <FxIcon
                        name="description"
                        size={22}
                        className="flex-none text-primary"
                      />
                      <Link
                        to={`/templates/${template.id}`}
                        className={cn(
                          'min-w-0 flex-1 truncate text-body text-text no-underline',
                          'hover:text-primary-text hover:underline',
                        )}
                      >
                        {template.title}
                      </Link>
                      <span className="hidden w-[140px] flex-none text-small text-text-muted sm:block">
                        {documentCountLabel(template.documentCount ?? 0)}
                      </span>
                      <span className="hidden w-[110px] flex-none text-right text-small tabular-nums text-text-muted xs:block">
                        {formatDate(template.updatedAt)}
                      </span>
                      {/* The kebab and bookmark columns are the project's;
                          a form's own actions live on the form itself. The
                          spacer keeps the two levels' columns aligned. */}
                      <span aria-hidden className="w-[72px] flex-none" />
                    </li>
                  ))
                )}
              </ul>
            )}
          </li>
        )
      })}
    </ul>
  )
}
